import * as React from "react";
import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import {
  fetchAdminQuote,
  updateAdminQuote,
  deleteAdminQuote,
  fetchAdminQuoteStats,
  QuoteStats,
} from "../api";
import AdminQuoteForm, { QuoteFormValues } from "../components/Admin/AdminQuoteForm";
import PageHero from "../components/common/PageHero";
import { formatISTDate } from "../utils/date";

import type { Quote } from "../api";

export default function AdminQuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = React.useState<Quote | null>(null);
  const [stats, setStats] = React.useState<QuoteStats | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);

  const loadQuote = React.useCallback(async () => {
    if (!id) {
      setQuote(null);
      setStats(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [quoteData, statsData] = await Promise.all([
        fetchAdminQuote(id),
        fetchAdminQuoteStats(id).catch(() => null),
      ]);
      setQuote(quoteData ?? null);
      setStats((statsData as QuoteStats | null) ?? null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  const handleUpdate = async (data: QuoteFormValues) => {
    if (!id) return;
    setSaving(true);
    try {
      await updateAdminQuote(id, data);
      await loadQuote();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || deleting) return;
    if (window.confirm("Are you sure you want to delete this quote?")) {
      setDeleting(true);
      try {
        await deleteAdminQuote(id);
        navigate("/quotes");
      } finally {
        setDeleting(false);
      }
    }
  };

  const heroDescription = quote
    ? `Scheduled for ${formatISTDate(quote.date)}${quote.author ? `, shared by ${quote.author}` : ""}.`
    : "Refine the meditation that appears across the dashboard.";

  let content: React.ReactNode;

  if (loading) {
    content = (
      <Card
        variant="outlined"
        sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, display: "flex", justifyContent: "center" }}
      >
        <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Loading quote…
          </Typography>
        </Stack>
      </Card>
    );
  } else if (!quote) {
    content = (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Avatar variant="soft" color="primary" sx={{ width: 56, height: 56, borderRadius: "lg" }}>
            <FormatQuoteRoundedIcon />
          </Avatar>
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Quote not found
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
            It may have been removed or shared under a different link. Return to the library to manage other reflections.
          </Typography>
          <Button component={RouterLink} to="/quotes" variant="outlined" color="neutral" startDecorator={<ArrowBackRoundedIcon />}>
            Back to library
          </Button>
        </Stack>
      </Card>
    );
  } else {
    content = (
      <Grid container spacing={2.5}>
        <Grid xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, gap: 2.5 }}>
            <Stack spacing={0.75}>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                Update reflection
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Tweak the wording or citation to keep this meditation clear and devotional for everyone.
              </Typography>
            </Stack>

            <AdminQuoteForm
              initial={{ text: quote.text, author: quote.author ?? "", date: quote.date }}
              onSubmit={handleUpdate}
              submitLabel="Save changes"
              loading={saving}
            />
          </Card>
        </Grid>
        <Grid xs={12} md={4}>
          <Stack spacing={2.5}>
            <Card
              variant="soft"
              color="primary"
              sx={{
                borderRadius: "xl",
                p: 2.5,
                gap: 1.25,
                boxShadow: "0 18px 40px rgba(13, 109, 219, 0.18)",
              }}
            >
              <FormatQuoteRoundedIcon />
              <Typography level="body-md" sx={{ fontWeight: 600 }}>
                “{quote.text}”
              </Typography>
              <Typography level="body-sm" sx={{ color: "rgba(255,255,255,0.78)" }}>
                {quote.author ? `— ${quote.author}` : "Shared anonymously"}
              </Typography>
              <Typography level="body-xs" sx={{ color: "rgba(255,255,255,0.78)" }}>
                Scheduled for {formatISTDate(quote.date)}
              </Typography>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: "xl", p: 2.5, gap: 1 }}>
              <Typography level="body-xs" sx={{ textTransform: "uppercase", letterSpacing: 0.8, color: "text.tertiary" }}>
                Engagement
              </Typography>
              <Typography level="h3" sx={{ fontWeight: 700 }}>
                {stats?.viewCount ?? 0}
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Views recorded for this reflection.
              </Typography>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Stack spacing={3}>
      <PageHero
        eyebrow="Daily reflections"
        title="Edit quote"
        description={heroDescription}
        actions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              component={RouterLink}
              to="/quotes"
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBackRoundedIcon />}
            >
              Back to library
            </Button>
            <Button
              color="danger"
              variant="solid"
              startDecorator={<DeleteOutlineRoundedIcon />}
              onClick={handleDelete}
              loading={deleting}
              disabled={!quote}
              data-testid="delete-quote"
            >
              Delete quote
            </Button>
          </Stack>
        }
      />

      {content}
    </Stack>
  );
}
